import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import config from "../common/Config";
import "../common/commonStyles.css";
import { buildDatatablePayload, postDashboardDatatable } from "../../utils/dashboardApi";

const DeliveryConfirmed = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const s = localStorage.getItem('startDate');
  const e = localStorage.getItem('endDate');

  const formatDate = (date) => {
    if (!date) return "0";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const startOfMonth = new Date(year, month, 1);
    const startDay = String(startOfMonth.getDate()).padStart(2, "0");
    const startMonth = String(startOfMonth.getMonth() + 1).padStart(2, "0");

    const endOfMonth = new Date(year, month + 1, 0);
    const endDay = String(endOfMonth.getDate()).padStart(2, "0");
    const endMonth = String(endOfMonth.getMonth() + 1).padStart(2, "0");

    const startDateFormatted = `${startDay}-${startMonth}-${year}`;
    const endDateFormatted = `${endDay}-${endMonth}-${year}`;

    return { startDateFormatted, endDateFormatted };
  };

  const { startDateFormatted, endDateFormatted } = getCurrentMonthDates();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(
        token,
        'get_deliveredInvoice',
        buildDatatablePayload({
          page,
          rowsPerPage,
          startDate: s,
          endDate: e,
          searchValue,
        })
      );

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      }
    } catch (error) {
      console.error('Error fetching delivery confirmed data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, s, e, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const formatRowDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <Box className="page-container" >
        <Paper className="paper-style">
          <Box sx={{ px:2, py:1.2}}>
            <Grid2
              container
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Delivery Confirmed
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ gap: 1, display: 'flex', flexDirection: {'xs': 'column', 'md': 'row'}, justifyContent: 'space-between', alignItems: 'center' }}> 
                <TextField
                  fullWidth
                  className="input-border"
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "#212b36", width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography
                  align="right"
                  sx={{
                    color: "#212b36",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  From: {s ? formatDate(s) : startDateFormatted} &nbsp;
                  To: {e ? formatDate(e) : endDateFormatted}
                </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer >
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            )}
            <Table
              size="small"
              className="table-cell-border"
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell
                    width="100px"
                  >
                    Order Date
                  </TableCell>
                  <TableCell
                    width="80px"
                  >
                    Order Id
                  </TableCell>
                  <TableCell
                    align="left"
                    width="200px"
                  >
                    Customers (A/C No)
                  </TableCell>
                  <TableCell
                    align="right"
                    width="100px"
                  >
                    Order Amt
                  </TableCell>
                  <TableCell
                    align="right"
                    width="100px"
                  >
                    Rep Cash
                  </TableCell>
                  <TableCell
                    align="right"
                    width="100px"
                  >
                    Previous Dues
                  </TableCell>
                  <TableCell
                    align="center"
                    width="100px"
                  >
                    Route
                  </TableCell>
                  <TableCell
                    align="left"
                    width="100px"
                  >
                    Order By
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && data && data.length > 0 ? (
                  data.map((row, index) => (
                    <TableRow
                      key={index}
                      hover
                      className="table-cell-style"
                    >
                      <TableCell
                      >
                        {formatRowDate(row.date)}
                      </TableCell>
                      <TableCell    
                      >
                        {row.reference_no}
                      </TableCell>
                      <TableCell
                        width="200px"
                      >
                        {row.customer}
                      </TableCell>
                      <TableCell
                        align="right"
                      >
                        {row.grand_total}
                      </TableCell>
                      <TableCell
                        align="right"
                    
                      >
                        {row.spc}
                      </TableCell>
                      <TableCell
                        align="right"
                 
                      >
                        {row.prev_due}
                      </TableCell>
                      <TableCell
                        align="center"
                  
                      >
                        {row.route_number}
                      </TableCell>
                      <TableCell
                        align="left"
                 
                      >
                        {row.name}
                      </TableCell>
                    </TableRow>
                  ))
                ) : !loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{ py: 8 }}
                    >
                      <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              className="pagination-styles"
            />
          )}
        </Paper>
      </Box>
    </>
  );
};

export default DeliveryConfirmed;
